import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../store/authContext';
import { useUsage, UsageMeter } from '../lib/hooks';
import api from '../lib/api';
import Footer from '../components/Footer';

// Icons
const ChartBarIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const MailIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const CurrencyDollarIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const SparklesIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { usage } = useUsage();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await api.get('/analytics/overview');
      return response.data;
    }
  });

  const navigation = [
    { name: 'Dashboard', href: '/', icon: ChartBarIcon, current: true },
    { name: 'Campaigns', href: '/campaigns', icon: MailIcon, current: false },
    { name: 'Contacts', href: '/contacts', icon: UsersIcon, current: false },
    { name: 'Analytics', href: '/analytics', icon: ChartBarIcon, current: false },
    { name: 'AI Assistant', href: '/ai', icon: SparklesIcon, current: false },
    { name: 'Automation', href: '/automation', icon: ChartBarIcon, current: false },
    { name: 'Landing Pages', href: '/pages', icon: ChartBarIcon, current: false },
    { name: 'Billing', href: '/billing', icon: CurrencyDollarIcon, current: false },
    { name: 'Settings', href: '/settings', icon: ChartBarIcon, current: false },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 transition-transform duration-300`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-blue-600">VMAIL</h1>
            <button onClick={() => setSidebarOpen(false)} className="md:hidden">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  item.current
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <item.icon />
                <span className="ml-3">{item.name}</span>
              </Link>
            ))}
          </nav>

          {/* User */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                  {user?.first_name?.[0] || user?.email?.[0] || '?'}
                </div>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.first_name || user?.email || 'User'}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
              </div>
              <button onClick={logout} className="ml-2 text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="md:ml-64">
        {/* Top Bar */}
        <header className="bg-white shadow-sm">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button onClick={() => setSidebarOpen(true)} className="md:hidden">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex-1 flex justify-end">
              <Link
                to="/campaigns/new"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon />
                <span className="ml-2">New Campaign</span>
              </Link>
            </div>
          </div>
        </header>

        <main className="p-6">
          {/* Welcome */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900">
              Welcome back{user?.first_name ? `, ${user.first_name}` : ''}!
            </h2>
            <p className="mt-1 text-gray-600">Here's what's happening with your email marketing today.</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
            <StatCard
              title="Total Campaigns"
              value={stats?.data?.campaigns?.total || 0}
              icon={MailIcon}
              color="blue"
              change="+12%"
            />
            <StatCard
              title="Emails Sent"
              value={stats?.data?.campaigns?.sent || 0}
              icon={MailIcon}
              color="green"
              change="+23%"
            />
            <StatCard
              title="Avg Open Rate"
              value={`${(stats?.data?.campaigns?.avg_open_rate || 0).toFixed(1)}%`}
              icon={ChartBarIcon}
              color="purple"
              change="+5%"
            />
            <StatCard
              title="Total Contacts"
              value={stats?.data?.contacts?.total || 0}
              icon={UsersIcon}
              color="orange"
              change="+18%"
            />
          </div>

          {/* Usage Meter */}
          {usage && (
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Usage</h3>
              <UsageMeter
                label="Emails Sent"
                current={usage.emails.sent}
                limit={usage.emails.limit}
                unlimited={usage.emails.unlimited}
                percentage={usage.emails.percentage}
              />
              <UsageMeter
                label="Contacts"
                current={usage.contacts.count}
                limit={usage.contacts.limit}
                unlimited={usage.contacts.unlimited}
                percentage={usage.contacts.percentage}
              />
              {usage.campaigns.daily_limit > 0 && (
                <UsageMeter
                  label="Campaigns Today"
                  current={usage.campaigns.today}
                  limit={usage.campaigns.daily_limit}
                  unlimited={usage.campaigns.unlimited}
                  percentage={usage.campaigns.percentage}
                />
              )}
              {usage.emails.percentage > 80 && (
                <Link
                  to="/billing"
                  className="inline-block mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                >
                  Upgrade Plan
                </Link>
              )}
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <QuickActionLink to="/campaigns/new" title="Create Campaign" description="Start a new email campaign" />
                <QuickActionLink to="/contacts/import" title="Import Contacts" description="Upload your contact list" />
                <QuickActionLink to="/templates" title="Browse Templates" description="Choose from pre-designed templates" />
                <QuickActionLink to="/automation" title="Setup Automation" description="Create automated email sequences" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Campaigns</h3>
              <RecentCampaigns />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color, change }: {
  title: string;
  value: string | number;
  icon: any;
  color: string;
  change?: string;
}) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 transition-shadow hover:shadow-md">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          {change && (
            <p className="text-sm text-green-600 mt-2">{change} from last month</p>
          )}
        </div>
        <div className={`p-4 rounded-full ${colorClasses[color as keyof typeof colorClasses]}`}>
          <Icon />
        </div>
      </div>
    </div>
  );
}

function QuickActionLink({ to, title, description }: {
  to: string;
  title: string;
  description: string;
}) {
  return (
    <Link to={to} className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
      <h4 className="font-medium text-gray-900">{title}</h4>
      <p className="text-sm text-gray-500 mt-1">{description}</p>
    </Link>
  );
}

function RecentCampaigns() {
  const { data } = useQuery({
    queryKey: ['recent-campaigns'],
    queryFn: async () => {
      const response = await api.get('/campaigns', { params: { per_page: 5 } });
      return response.data;
    }
  });

  if (!data?.data?.items?.length) {
    return (
      <div className="text-center py-8">
        <MailIcon />
        <p className="text-gray-500 mt-2">No campaigns yet</p>
        <Link to="/campaigns/new" className="text-blue-600 hover:text-blue-700 text-sm mt-2 inline-block">
          Create your first campaign →
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {data.data.items.map((campaign: any) => (
        <div key={campaign.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <p className="font-medium text-gray-900">{campaign.name}</p>
            <p className="text-sm text-gray-500">{campaign.subject}</p>
          </div>
          <div className="text-right">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              campaign.status === 'completed' ? 'bg-green-100 text-green-800' :
              campaign.status === 'sending' ? 'bg-blue-100 text-blue-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {campaign.status}
            </span>
            <p className="text-xs text-gray-500 mt-1">{campaign.sent_count || 0} sent</p>
          </div>
        </div>
      ))}
    </div>
  );
}
